/**
 * AllBlogs - "Load More" Functionality for Blog Articles
 *
 * This script powers a "Load More" button for a blog post grid. It fetches
 * additional articles via Shopify's Section Rendering API and appends them
 * to the grid.
 */
class AllBlogs extends HTMLElement {
  constructor() {
    super();

    this.grid = this.querySelector('.grid');
    this.loadMoreButton = this.querySelector('.button');
    this.loadingSpinner = this.querySelector('.guodu');

    if (!this.grid || !this.loadMoreButton || !this.loadingSpinner) {
      console.error('Load More Blogs: Missing required elements (.grid, .button, or .guodu).');
      return;
    }

    const remainingArticlesData = this.dataset.remainingArticles || '';
    this.remainingArticles = remainingArticlesData ? remainingArticlesData.split(',').filter(a => a) : [];
    this.articlesToLoad = parseInt(this.dataset.articlesToLoad, 10) || 3;
    this.sectionId = this.dataset.sectionId;
    this.blogUrl = this.dataset.blogUrl; // 获取博客的基础 URL

    this.loadMoreButton.addEventListener('click', this.loadMore.bind(this));
    this.updateButtonVisibility();
  }

  loadMore() {
    this.setLoading(true);
    const articlesToFetch = this.remainingArticles.splice(0, this.articlesToLoad);

    if (articlesToFetch.length === 0) {
      this.setLoading(false);
      this.updateButtonVisibility();
      return;
    }

    // 【核心区别】
    // 对于博客文章，我们不能像产品一样使用 `filter.p.handle`。
    // 我们需要请求每一篇单独的文章 URL，并带上 section_id。
    // 然后将所有请求的结果合并。
    
    const fetchPromises = articlesToFetch.map(handle => {
      const articleUrl = `${this.blogUrl}/${handle}`; // 构建单篇文章的 URL
      const url = `${articleUrl}?section_id=${this.sectionId}`;
      return fetch(url).then(response => {
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.text();
      });
    });

    Promise.all(fetchPromises)
      .then(htmls => {
        const parser = new DOMParser();
        htmls.forEach(html => {
          const doc = parser.parseFromString(html, 'text/html');
          const newArticle = doc.querySelector('.blog-card-wrapper');
          if (newArticle) {
            this.grid.appendChild(newArticle);
          }
        });
      })
      .catch(error => {
        console.error('Error loading more articles:', error);
        this.remainingArticles.unshift(...articlesToFetch);
      })
      .finally(() => {
        this.setLoading(false);
        this.updateButtonVisibility();
      });
  }

  setLoading(isLoading) {
    if (isLoading) {
      this.loadMoreButton.style.display = 'none';
      this.loadingSpinner.style.display = 'block';
    } else {
      this.loadingSpinner.style.display = 'none';
    }
  }

  updateButtonVisibility() {
    const hasMoreArticles = this.remainingArticles.length > 0;
    this.loadMoreButton.style.display = hasMoreArticles ? 'block' : 'none';
  }
}

// Define the custom element once the DOM is ready.
if (!customElements.get('all-blogs')) {
  customElements.define('all-blogs', AllBlogs);
}